import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, KeyRound, Trash2 } from 'lucide-react';
import { MediaProvider } from '@mg/shared';
import type { MediaCredential } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useIsWorkspaceAdmin } from '../../auth/useWorkspaceAdmin';
import { useTenantKey } from '../../lib/useTenantKey';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { FormField, Input } from '../../components/ui/Field';
import { useFieldId } from '../../components/ui/useFieldId';
import { useDialogs } from '../../components/ui/DialogProvider';
import { CHANGES_SAVED, useToast } from '../../components/ui/toast';
import { useSaveHold } from '../../components/settings/footerSave';
import { MEDIA_PROVIDERS, mediaProviderLabel } from './mediaDisplay';

/**
 * Bring-your-own media-generator API keys. One row per supported provider, showing
 * whether a key is stored (masked) and an expandable form to add or replace it. The keys
 * are SITE-WIDE: one per provider, shared by every workspace. Any member may see which
 * providers are connected; only a Tenant Admin may save or remove a key. Keys are
 * write-only — the value is never returned, only the Connected / Not set state + label.
 * Mirrors the Apify credential card on this page and shares the
 * `['media-credentials', tenantKey]` cache with the Generate modal, so connecting a
 * provider here immediately offers it there.
 */
export function MediaGeneratorsCard() {
  const tenantKey = useTenantKey();
  // Hides Remove from people the API would refuse. The API is the real check: saving or
  // removing a site-wide key is Tenant-Admin-only.
  const isAdmin = useIsWorkspaceAdmin();

  const credentialsQuery = useQuery({
    queryKey: ['media-credentials', tenantKey],
    queryFn: ({ signal }) => api.listMediaCredentials(signal),
    enabled: tenantKey !== '',
  });
  const credentials = credentialsQuery.data ?? [];

  return (
    <Card>
      <CardHeader
        title="Media Generators"
        description="Bring your own image-model API keys to generate media with them in the Media Library."
      />
      <CardBody className="space-y-4">
        <p className="text-sm text-ink-muted">
          Smorgas Social&apos;s built-in FLUX model needs no key of yours; a Tenant Admin switches it
          on or off for this install in App Settings → AI models. Connect a provider below to
          generate with your own account instead. Most providers need only an API key; Magnific
          also needs a webhook signing secret.
        </p>

        {credentialsQuery.isError ? (
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load your media generators.</p>
        ) : (
          <ul className="space-y-3">
            {MEDIA_PROVIDERS.map((provider) => (
              <ProviderRow
                key={provider}
                provider={provider}
                credentials={credentials.filter((c) => c.provider === provider)}
                isAdmin={isAdmin}
                tenantKey={tenantKey}
              />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function ProviderRow({
  provider,
  credentials,
  isAdmin,
  tenantKey,
}: {
  provider: MediaProvider;
  credentials: MediaCredential[];
  isAdmin: boolean;
  tenantKey: string;
}) {
  const queryClient = useQueryClient();
  const { confirm, alert } = useDialogs();
  const toast = useToast();
  // On Site Integrations, leaving the tab or the page waits for a save or a removal still running here,
  // so its answer (a failure above all) is never lost with the card.
  const hold = useSaveHold();
  const [open, setOpen] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [error, setError] = useState<string | null>(null);

  const keyId = useFieldId('media-key');
  const secretId = useFieldId('media-secret');

  const isMagnific = provider === MediaProvider.MAGNIFIC;
  const label = mediaProviderLabel[provider];
  const connected = credentials.filter((c) => c.hasKey);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['media-credentials', tenantKey] });
    // Refresh the Site Integrations "Recent site changes" log (save/remove are audited).
    void queryClient.invalidateQueries({ queryKey: ['site-audit'] });
  };

  const reset = () => {
    setApiKey('');
    setWebhookSecret('');
    setError(null);
    setOpen(false);
  };

  const save = useMutation({
    mutationFn: () =>
      api.saveMediaCredential({
        provider,
        apiKey: apiKey.trim(),
        webhookSecret:
          isMagnific && webhookSecret.trim() ? webhookSecret.trim() : undefined,
      }),
    onSuccess: () => {
      void invalidate();
      reset();
      toast.show(CHANGES_SAVED);
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not save this key.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteMediaCredential(id),
    onSuccess: () => {
      void invalidate();
      toast.show(CHANGES_SAVED);
    },
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: err instanceof ApiRequestError ? err.message : 'Could not remove this key.',
      }),
  });

  // Magnific requires both fields; other providers just the API key.
  const canSave =
    apiKey.trim() !== '' && (!isMagnific || webhookSecret.trim() !== '');

  return (
    <li className="rounded-lg border border-line px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink">{label}</span>
          {connected.length > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-success-tint dark:bg-success-dark-tint px-2 py-0.5 text-xs font-medium text-success dark:text-success-dark">
              <Check className="h-3.5 w-3.5" aria-hidden /> Connected
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-chip px-2 py-0.5 text-xs font-medium text-ink-muted">
              Not set
            </span>
          )}
        </div>
        <Button variant="secondary" size="sm" onClick={() => setOpen((v) => !v)}>
          <KeyRound className="h-4 w-4" aria-hidden />
          {connected.length > 0 ? 'Add / replace key' : 'Add key'}
        </Button>
      </div>

      {connected.length > 0 && (
        <ul className="mt-3 space-y-2">
          {connected.map((cred) => {
            // Every key listed is site-wide (there are no personal or per-workspace keys any
            // more), and removing one is an admin write — gate the button to avoid a sure 403.
            const scopeLabel = 'Site-wide';
            const canRemove = isAdmin;
            return (
              <li
                key={cred.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-page px-3 py-2"
              >
                <p className="min-w-0 text-xs text-ink-label">
                  <span className="font-medium text-ink-strong">{scopeLabel}</span>
                  {cred.label ? ` · ${cred.label}` : ''}
                  {isMagnific && (
                    <span className={cred.hasWebhookSecret ? 'text-success dark:text-success-dark' : 'text-amber-700 dark:text-warning-dark'}>
                      {' · '}
                      webhook secret {cred.hasWebhookSecret ? 'set' : 'missing'}
                    </span>
                  )}
                </p>
                {canRemove && (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={remove.isPending && remove.variables === cred.id}
                    onClick={async () => {
                      if (
                        await confirm({
                          title: 'Remove key',
                          message: `Remove the site-wide ${label} key?`,
                          confirmLabel: 'Remove',
                          danger: true,
                        })
                      ) {
                        void hold(remove.mutateAsync(cred.id));
                      }
                    }}
                    className="text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden /> Remove
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {open && (
        <div className="mt-3 space-y-3 border-t border-line-soft pt-3">
          <FormField
            label={`${label} API key`}
            htmlFor={keyId}
            hint="Kept on our server and used only for your generations. Write-only — it is never shown again."
          >
            <Input
              id={keyId}
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => {
                setError(null);
                setApiKey(e.target.value);
              }}
              placeholder="Paste your API key"
            />
          </FormField>

          {isMagnific && (
            <FormField
              label="Webhook signing secret"
              htmlFor={secretId}
              hint="Magnific returns results asynchronously — this secret verifies the callbacks it posts back."
            >
              <Input
                id={secretId}
                type="password"
                autoComplete="off"
                value={webhookSecret}
                onChange={(e) => {
                  setError(null);
                  setWebhookSecret(e.target.value);
                }}
                placeholder="Paste the webhook signing secret"
              />
            </FormField>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              loading={save.isPending}
              disabled={save.isPending || !canSave}
              onClick={() => {
                setError(null);
                void hold(save.mutateAsync());
              }}
            >
              Save key
            </Button>
            <Button variant="ghost" size="sm" onClick={reset} disabled={save.isPending}>
              Cancel
            </Button>
            {error && <span className="text-sm text-red-600 dark:text-error-dark">{error}</span>}
          </div>
        </div>
      )}
    </li>
  );
}
